"use client"

import { useEffect, useState } from "react"
import { ArrowUpRight } from "lucide-react"
import { apiUrl, authHeaders } from "@/lib/api"
import Sparkline from "./sparkline"

interface Store {
  id: number
  name: string
  enabled: boolean
  frequency?: string
  min_discount_percentage?: number
  total_detected?: number
  detected_today?: number
  locked_at?: string | null
}

interface ProductLite {
  created_at?: string
  store?: { id?: number; name?: string } | null
}

function formatFrequency(freq?: string): string {
  if (!freq) return "—"
  const map: Record<string, string> = {
    "5min": "5 min", "10min": "10 min", "15min": "15 min", "30min": "30 min",
    "1h": "1h", "2h": "2h", "6h": "6h", "12h": "12h", "24h": "24h",
  }
  return map[freq] ?? freq
}

function timeAgo(date?: string | null): string {
  if (!date) return "—"
  const min = Math.floor((Date.now() - new Date(date).getTime()) / 60000)
  if (min < 1) return "agora"
  if (min < 60) return `${min}m atrás`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h atrás`
  return `${Math.floor(h / 24)}d atrás`
}

function buildDailySeries(products: ProductLite[], storeName: string): number[] {
  const buckets = new Array(14).fill(0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startTime = today.getTime() - 13 * 24 * 60 * 60 * 1000

  const slug = storeName.toLowerCase().slice(0, 5)
  for (const p of products) {
    if (!p.created_at) continue
    const nome = (p.store?.name || "").toLowerCase()
    if (!nome.includes(slug)) continue
    const t = new Date(p.created_at).getTime()
    if (t < startTime) continue
    const day = Math.floor((t - startTime) / (24 * 60 * 60 * 1000))
    if (day >= 0 && day < 14) buckets[day]++
  }
  return buckets
}

function lastCollectForStore(products: ProductLite[], storeName: string): string | null {
  const slug = storeName.toLowerCase().slice(0, 5)
  for (const p of products) {
    if (!p.created_at) continue
    const nome = (p.store?.name || "").toLowerCase()
    if (nome.includes(slug)) return p.created_at
  }
  return null
}

export default function ContentCategories() {
  const [stores, setStores] = useState<Store[]>([])
  const [products, setProducts] = useState<ProductLite[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    const headers = authHeaders(token)
    Promise.all([
      fetch(`${apiUrl()}/stores/`, { headers }).then((r) => r.json()),
      fetch(`${apiUrl()}/products/?page_size=400&ordering=-created_at`, { headers }).then((r) => r.json()),
    ])
      .then(([storesData, prodData]) => {
        setStores(storesData.results || (Array.isArray(storesData) ? storesData : []))
        setProducts(prodData.results || (Array.isArray(prodData) ? prodData : []))
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const totalDetectados = stores.reduce((a, s) => a + (s.total_detected || 0), 0)
  const ativasCount = stores.filter((s) => s.enabled).length

  return (
    <div className="surface-card flex h-full flex-col">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Fontes</div>
          <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">Lojas</h3>
        </div>
        <a
          href="/dashboard-cms/lojas"
          className="group inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Detalhes
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>

      <div className="mt-5 flex-1">
        {loading ? (
          <div className="space-y-5">
            {[...Array(2)].map((_, i) => <div key={i} className="h-40 animate-pulse rounded bg-muted/30" />)}
          </div>
        ) : stores.length === 0 ? (
          <div className="py-8 text-sm text-muted-foreground">Nenhuma loja cadastrada.</div>
        ) : (
          <ul className="divide-y divide-border/40">
            {stores.map((store) => {
              const series = buildDailySeries(products, store.name)
              const lastCollect = lastCollectForStore(products, store.name)
              const lastIdx = series.length - 1
              const todayCount = series[lastIdx] ?? 0
              const yesterdayCount = series[lastIdx - 1] ?? 0
              const delta = todayCount - yesterdayCount
              return (
                <li key={store.id} className="py-5 first:pt-0 last:pb-0">
                  <div className="flex items-baseline justify-between">
                    <h4 className="text-base font-semibold tracking-tight text-foreground">
                      {store.name}
                    </h4>
                    <div className="flex items-center gap-1.5">
                      <span className={`h-1.5 w-1.5 rounded-full ${store.enabled ? "bg-emerald-500" : "bg-muted-foreground/50"}`} />
                      <span className={`text-[10px] uppercase tracking-[0.14em] ${store.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                        {store.enabled ? "ativa" : "inativa"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 flex items-end justify-between gap-3">
                    <div className="flex-1">
                      <Sparkline data={series} width="100%" height={32} strokeWidth={1.5} color="var(--brand-amber)" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">14 dias</span>
                  </div>

                  <dl className="mt-4 grid grid-cols-4 gap-x-3 gap-y-1">
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">total</dt>
                      <dd className="mt-0.5 text-sm font-semibold text-foreground tabular-nums">
                        {(store.total_detected || 0) >= 1000
                          ? `${((store.total_detected || 0) / 1000).toFixed(1)}K`
                          : (store.total_detected || 0).toLocaleString("pt-BR")}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">hoje</dt>
                      <dd className="mt-0.5 flex items-baseline gap-1 text-sm font-semibold text-foreground tabular-nums">
                        {store.detected_today ?? 0}
                        {delta !== 0 && Math.abs(delta) >= 5 && (
                          <span className={`text-[10px] font-normal ${delta > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                            {delta > 0 ? "+" : ""}{delta}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">ciclo</dt>
                      <dd className="mt-0.5 text-sm font-semibold text-foreground tabular-nums">
                        {formatFrequency(store.frequency)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">mín</dt>
                      <dd className="mt-0.5 text-sm font-semibold text-foreground tabular-nums">
                        {store.min_discount_percentage != null && store.min_discount_percentage > 0
                          ? `${store.min_discount_percentage}%`
                          : "—"}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-3 text-xs text-muted-foreground">
                    última leitura <span className="text-foreground tabular-nums">{timeAgo(lastCollect)}</span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border/40 pt-4 text-xs text-muted-foreground tabular-nums">
        <span><span className="text-foreground">{ativasCount}</span>/{stores.length} ativas</span>
        <span><span className="text-foreground">{totalDetectados.toLocaleString("pt-BR")}</span> capturados</span>
      </div>
    </div>
  )
}
